import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { memo, useCallback, useState } from 'react';
const POOL_PRESETS = [
    {
        name: 'Fringe',
        allocationBase: 'direct_labor',
        appliesTo: [
            'labor'
        ]
    },
    {
        name: 'Overhead',
        allocationBase: 'direct_labor_plus_fringe',
        appliesTo: [
            'labor'
        ]
    },
    {
        name: 'G&A',
        allocationBase: 'total_cost_input',
        appliesTo: [
            'labor',
            'travel',
            'odc'
        ]
    },
    {
        name: 'Facilities',
        allocationBase: 'direct_labor',
        appliesTo: [
            'labor'
        ]
    },
    {
        name: 'Material Handling',
        allocationBase: 'total_cost_input',
        appliesTo: [
            'material'
        ]
    }
];
function formatPercent(rate) {
    return `${(rate * 100).toFixed(1)}%`;
}
function PoolRow({ pool, onUpdate, onRemove }) {
    const [editingRate, setEditingRate] = useState(false);
    const [rateValue, setRateValue] = useState('');
    const handleStartEdit = useCallback(()=>{
        setRateValue(String((pool.ratePercent * 100).toFixed(1)));
        setEditingRate(true);
    }, [
        pool.ratePercent
    ]);
    const handleBlur = useCallback(()=>{
        setEditingRate(false);
        const parsed = Number(rateValue);
        if (Number.isNaN(parsed) || parsed < 0) return;
        const newRate = parsed / 100;
        if (newRate !== pool.ratePercent) {
            onUpdate({
                ...pool,
                ratePercent: newRate
            });
        }
    }, [
        rateValue,
        pool,
        onUpdate
    ]);
    const handleKeyDown = useCallback((e)=>{
        if (e.key === 'Enter') e.target.blur();
        else if (e.key === 'Escape') setEditingRate(false);
    }, []);
    return /*#__PURE__*/ _jsxs("tr", {
        className: "border-b border-gray-100 hover:bg-gray-50/50 group",
        children: [
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-2 text-sm text-gray-400 w-8",
                children: /*#__PURE__*/ _jsx(GripVertical, {
                    className: "w-4 h-4 opacity-0 group-hover:opacity-100"
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-2 text-sm font-medium text-gray-900",
                children: pool.poolName
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-2 text-sm text-right",
                children: editingRate ? /*#__PURE__*/ _jsx("input", {
                    type: "number",
                    value: rateValue,
                    onChange: (e)=>setRateValue(e.target.value),
                    onBlur: handleBlur,
                    onKeyDown: handleKeyDown,
                    className: "w-20 px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-right",
                    step: "0.1"
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "cursor-pointer hover:bg-gray-100 px-2 py-1 rounded",
                    onClick: handleStartEdit,
                    children: formatPercent(pool.ratePercent)
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-2 text-sm text-gray-500",
                children: pool.allocationBase.replace(/_/g, ' ')
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-2 text-sm text-gray-500",
                children: pool.appliesToCostTypes.join(', ')
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-2 text-center",
                children: /*#__PURE__*/ _jsx("button", {
                    onClick: onRemove,
                    className: "text-gray-300 hover:text-red-500 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                    children: /*#__PURE__*/ _jsx(Trash2, {
                        className: "w-4 h-4"
                    })
                })
            })
        ]
    });
}
export const IndirectRatePanel = /*#__PURE__*/ memo(function IndirectRatePanel({ config, onChange }) {
    const [showPresets, setShowPresets] = useState(false);
    const pools = config?.pools ?? [];
    const useMultiplicative = config?.useMultiplicative ?? true;
    const ensureConfig = useCallback(()=>{
        return config ?? {
            pools: [],
            useMultiplicative: true,
            gaBaseMethod: 'total_cost_input'
        };
    }, [
        config
    ]);
    const handleAddPool = useCallback((preset)=>{
        const cfg = ensureConfig();
        const newPool = {
            poolName: preset.name,
            ratePercent: 0,
            applyOrder: cfg.pools.length + 1,
            allocationBase: preset.allocationBase,
            appliesToCostTypes: preset.appliesTo
        };
        onChange({
            ...cfg,
            pools: [
                ...cfg.pools,
                newPool
            ]
        });
        setShowPresets(false);
    }, [
        ensureConfig,
        onChange
    ]);
    const handleUpdatePool = useCallback((index, updated)=>{
        const cfg = ensureConfig();
        const newPools = [
            ...cfg.pools
        ];
        newPools[index] = updated;
        onChange({
            ...cfg,
            pools: newPools
        });
    }, [
        ensureConfig,
        onChange
    ]);
    const handleRemovePool = useCallback((index)=>{
        const cfg = ensureConfig();
        const newPools = cfg.pools.filter((_, i)=>i !== index);
        // Re-index applyOrder
        newPools.forEach((p, i)=>{
            p.applyOrder = i + 1;
        });
        onChange({
            ...cfg,
            pools: newPools
        });
    }, [
        ensureConfig,
        onChange
    ]);
    const handleToggleMultiplicative = useCallback(()=>{
        const cfg = ensureConfig();
        onChange({
            ...cfg,
            useMultiplicative: !cfg.useMultiplicative
        });
    }, [
        ensureConfig,
        onChange
    ]);
    // Preview: compute wrap rate multiplier
    const wrapRate = (()=>{
        if (pools.length === 0) return 1;
        const laborPools = pools.filter((p)=>p.appliesToCostTypes.includes('labor')).sort((a, b)=>a.applyOrder - b.applyOrder);
        if (useMultiplicative) {
            let rate = 1.0;
            for (const pool of laborPools)rate *= 1 + pool.ratePercent;
            return rate;
        }
        return 1 + laborPools.reduce((s, p)=>s + p.ratePercent, 0);
    })();
    // Sample buildup for $100k base
    const sampleBase = 100000;
    const sampleBuildup = (()=>{
        const laborPools = pools.filter((p)=>p.appliesToCostTypes.includes('labor')).sort((a, b)=>a.applyOrder - b.applyOrder);
        const items = [
            {
                label: 'Direct Labor',
                amount: sampleBase
            }
        ];
        let base = sampleBase;
        for (const pool of laborPools){
            const amt = useMultiplicative ? base * pool.ratePercent : sampleBase * pool.ratePercent;
            items.push({
                label: pool.poolName,
                amount: amt
            });
            if (useMultiplicative) base += amt;
        }
        items.push({
            label: 'Total',
            amount: sampleBase * wrapRate
        });
        return items;
    })();
    const existingNames = new Set(pools.map((p)=>p.poolName));
    const availablePresets = POOL_PRESETS.filter((p)=>!existingNames.has(p.name));
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("label", {
                        className: "flex items-center gap-2 text-sm",
                        children: [
                            /*#__PURE__*/ _jsx("input", {
                                type: "checkbox",
                                checked: useMultiplicative,
                                onChange: handleToggleMultiplicative,
                                className: "rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-gray-700",
                                children: "Multiplicative cascading"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs text-gray-400",
                                children: "(DCAA standard)"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-sm text-gray-500",
                        children: [
                            "Wrap rate: ",
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-semibold text-gray-900",
                                children: [
                                    wrapRate.toFixed(4),
                                    "x"
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "bg-white border border-gray-200 rounded-lg overflow-hidden",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full border-collapse",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsxs("tr", {
                                className: "border-b-2 border-gray-200 bg-gray-50",
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "w-8"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                        children: "Pool"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28",
                                        children: "Rate %"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                        children: "Allocation Base"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                        children: "Applies To"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "w-10"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: pools.length === 0 ? /*#__PURE__*/ _jsx("tr", {
                                children: /*#__PURE__*/ _jsx("td", {
                                    colSpan: 6,
                                    className: "px-3 py-8 text-center text-sm text-gray-400",
                                    children: "No indirect rate pools configured. Add pools to compute wrap rates."
                                })
                            }) : pools.sort((a, b)=>a.applyOrder - b.applyOrder).map((pool, idx)=>/*#__PURE__*/ _jsx(PoolRow, {
                                    pool: pool,
                                    onUpdate: (updated)=>handleUpdatePool(idx, updated),
                                    onRemove: ()=>handleRemovePool(idx)
                                }, `${pool.poolName}-${idx}`))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        onClick: ()=>setShowPresets(!showPresets),
                        disabled: availablePresets.length === 0,
                        className: "flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed",
                        children: [
                            /*#__PURE__*/ _jsx(Plus, {
                                className: "w-4 h-4"
                            }),
                            "Add Pool"
                        ]
                    }),
                    showPresets && availablePresets.length > 0 && /*#__PURE__*/ _jsx("div", {
                        className: "absolute top-8 left-0 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[200px]",
                        children: availablePresets.map((preset)=>/*#__PURE__*/ _jsxs("button", {
                                onClick: ()=>handleAddPool(preset),
                                className: "w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50",
                                children: [
                                    preset.name,
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-xs text-gray-400 ml-2",
                                        children: [
                                            "(",
                                            preset.appliesTo.join(', '),
                                            ")"
                                        ]
                                    })
                                ]
                            }, preset.name))
                    })
                ]
            }),
            pools.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "bg-gray-50 border border-gray-200 rounded-lg p-4",
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-3",
                        children: "Sample Cost Buildup ($100k Direct Labor)"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "space-y-1",
                        children: sampleBuildup.map((item, idx)=>/*#__PURE__*/ _jsxs("div", {
                                className: `flex justify-between text-sm ${idx === sampleBuildup.length - 1 ? 'font-semibold text-gray-900 pt-1 border-t border-gray-200' : 'text-gray-600'}`,
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        children: item.label
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            "$",
                                            item.amount.toLocaleString(undefined, {
                                                maximumFractionDigits: 0
                                            })
                                        ]
                                    })
                                ]
                            }, item.label))
                    })
                ]
            })
        ]
    });
});
